import { type FieldType } from '../field.ts';
import { type AllValueJSON, type MutableValue, type Value } from '../value.ts';

export enum CollabFilterType {
  AND = 'and',
  OR = 'or',
  EQUALS = 'equals',
  CONTAINS = 'contains',
  EMPTY = 'empty',
  GREATER = 'greater',
  LESS = 'less'
}

export type CollabFilterJSONBooleanExpression = {
  id: string;
  type: CollabFilterType.AND | CollabFilterType.OR;
  value: CollabFilterJSON[];
};

export type CollabFilterJSONEquals = {
  id: string;
  type: CollabFilterType.EQUALS;
  not?: boolean;
  fieldId: string;
  value: AllValueJSON;
};

export type CollabFilterJSONContains = {
  id: string;
  type: CollabFilterType.CONTAINS;
  not?: boolean;
  fieldId: string;
  value: string;
};

export type CollabFilterJSONEmpty = {
  id: string;
  type: CollabFilterType.EMPTY;
  not?: boolean;
  fieldId: string;
};

export type CollabFilterJSONGreaterLess = {
  id: string;
  type: CollabFilterType.GREATER | CollabFilterType.LESS;
  orEqual?: boolean;
  fieldId: string;
  value: AllValueJSON;
};

export type CollabFilterJSON =
  | CollabFilterJSONBooleanExpression
  | CollabFilterJSONEquals
  | CollabFilterJSONContains
  | CollabFilterJSONEmpty
  | CollabFilterJSONGreaterLess;

export interface CollabFilterBooleanExpression {
  get id(): string;
  get type(): CollabFilterType.AND | CollabFilterType.OR;
  get value(): CollabFilter[];
  toJSON(): CollabFilterJSONBooleanExpression;
}

export interface MutableCollabFilterBooleanExpression extends CollabFilterBooleanExpression {
  add(filter: CollabFilterJSON): CollabFilter;
  remove(id: string, index: number): void;
  set id(val: string);
  set type(val: CollabFilterType.AND | CollabFilterType.OR);
  setFilter(id: string, index: number, val: CollabFilterJSON): void;
  get value(): MutableCollabFilter[];
}

export interface CollabFilterEquals {
  get id(): string;
  get type(): CollabFilterType.EQUALS;
  get not(): boolean;
  get fieldId(): string;
  getValue(): Value<FieldType> | undefined;
  toJSON(): CollabFilterJSONEquals;
}

export interface MutableCollabFilterEquals extends CollabFilterEquals {
  set id(val: string);
  set not(val: boolean);
  set fieldId(val: string);
  getValue(): MutableValue<FieldType> | undefined;
  setValue<T extends FieldType>(type: T): MutableValue<T>;
}

export interface CollabFilterContains {
  get id(): string;
  get type(): CollabFilterType.CONTAINS;
  get not(): boolean;
  get fieldId(): string;
  get value(): string;
  toJSON(): CollabFilterJSONContains;
}

export interface MutableCollabFilterContains extends CollabFilterContains {
  set id(val: string);
  set not(val: boolean);
  set fieldId(val: string);
  set value(val: string);
}

export interface CollabFilterEmpty {
  get id(): string;
  get type(): CollabFilterType.EMPTY;
  get not(): boolean;
  get fieldId(): string;
  toJSON(): CollabFilterJSONEmpty;
}

export interface MutableCollabFilterEmpty extends CollabFilterEmpty {
  set id(val: string);
  set not(val: boolean);
  set fieldId(val: string);
}

export interface CollabFilterGreaterLess {
  get id(): string;
  get type(): CollabFilterType.GREATER | CollabFilterType.LESS;
  get orEqual(): boolean;
  get fieldId(): string;
  getValue(): Value<FieldType> | undefined;
  toJSON(): CollabFilterJSONGreaterLess;
}

export interface MutableCollabFilterGreaterLess extends CollabFilterGreaterLess {
  set id(val: string);
  set orEqual(val: boolean);
  set fieldId(val: string);
  getValue(): MutableValue<FieldType> | undefined;
  setValue<T extends FieldType>(type: T): MutableValue<T>;
}

export type CollabFilter =
  | CollabFilterBooleanExpression
  | CollabFilterEquals
  | CollabFilterContains
  | CollabFilterEmpty
  | CollabFilterGreaterLess;
export type MutableCollabFilter =
  | MutableCollabFilterBooleanExpression
  | MutableCollabFilterEquals
  | MutableCollabFilterContains
  | MutableCollabFilterEmpty
  | MutableCollabFilterGreaterLess;
